import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type DragEvent } from "react";
import { Loader2, Play, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import VoiceGeographyFilter from "./VoiceGeographyFilter";
import { catalogVoiceName, EMPTY_VOICE_FILTERS, FILTER_LABELS, filterCatalogVoices, UNSPECIFIED, voiceFacetOptions, voiceGeographyOptions,
  type CatalogVoice, type VoiceFacet } from "@/utils/voiceCatalog";

interface Props {
  voices: CatalogVoice[];
  previewingVoiceId: string | null;
  previewStatus: "idle" | "loading" | "playing";
  previewError: string | null;
  onPreview: (voice: CatalogVoice) => void;
  onStop: () => void;
  onVoiceDragStart?: (voice: CatalogVoice, event: DragEvent<HTMLButtonElement>) => void;
  onVoiceDragEnd?: () => void;
  onSelectActor?: (voice: CatalogVoice) => void;
  defaultCountry?: string;
}

const countryAccentKeys = (voices: CatalogVoice[], country?: string) =>
  voiceGeographyOptions(voices).find(group => group.country === country)?.accents.map(accent => accent.key) ?? [];

export default function VoiceCatalogPanel({ voices, previewingVoiceId, previewStatus, previewError, onPreview, onStop, onVoiceDragStart, onVoiceDragEnd, onSelectActor, defaultCountry }: Props) {
  const id = useId();
  const [filters, setFilters] = useState(() => ({ ...EMPTY_VOICE_FILTERS, geography: countryAccentKeys(voices, defaultCountry) }));
  const defaultInitialized = useRef(voices.length > 0);
  useEffect(() => {
    if (defaultInitialized.current || !voices.length) return;
    defaultInitialized.current = true;
    const geography = countryAccentKeys(voices, defaultCountry);
    if (geography.length) setFilters(previous => ({ ...previous, geography }));
  }, [voices, defaultCountry]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const catalogPreviewId = useRef<string | null>(null);
  const filtered = useMemo(() => filterCatalogVoices(voices, filters), [voices, filters]);
  const facetOptions = useMemo(() => Object.fromEntries((Object.keys(FILTER_LABELS) as VoiceFacet[]).map(facet => [facet, voiceFacetOptions(voices, facet)])) as Record<VoiceFacet, string[]>, [voices]);
  const geographyOptions = useMemo(() => voiceGeographyOptions(voices), [voices]);
  const selected = filtered.find(voice => voice.id === selectedId) ?? filtered[0];
  const hasFilters = filters.geography.length > 0 || (Object.keys(FILTER_LABELS) as VoiceFacet[]).some(facet => filters[facet].length > 0);

  // Filtering out a playing voice must not leave hidden audio playing. Selection
  // itself stops synchronously, before starting a different row's preview.
  useEffect(() => {
    if (!previewingVoiceId || catalogPreviewId.current !== previewingVoiceId) {
      catalogPreviewId.current = null;
    } else if (!filtered.some(voice => voice.id === previewingVoiceId)) {
      catalogPreviewId.current = null;
      onStop();
    }
  }, [filtered, previewingVoiceId, onStop]);

  const selectVoice = (voice: CatalogVoice) => {
    if (voice.id !== selected?.id) onStop();
    setSelectedId(voice.id);
    onSelectActor?.(voice);
  };

  const navigateVoices = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const row = target.closest<HTMLElement>("[data-voice-row]");
    if (!row) return;
    const current = Number(row.dataset.voiceRow);
    const column = target.dataset.preview === "true" ? 1 : 0;
    let next = current;
    let nextColumn = column;
    if (event.key === "ArrowDown") next = Math.min(filtered.length - 1, current + 1);
    else if (event.key === "ArrowUp") next = Math.max(0, current - 1);
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = filtered.length - 1;
    else if (event.key === "ArrowRight") nextColumn = 1;
    else if (event.key === "ArrowLeft") nextColumn = 0;
    else return;
    event.preventDefault();
    const buttons = listRef.current?.querySelector(`[data-voice-row="${next}"]`)?.querySelectorAll<HTMLButtonElement>("button");
    const button = buttons?.[nextColumn];
    (button?.disabled ? buttons?.[0] : button)?.focus();
  };

  const renderFilter = (facet: VoiceFacet) => {
    const options = facetOptions[facet];
    const unknownOnly = options.length === 1 && options[0] === UNSPECIFIED;
    return <fieldset key={facet} className="min-w-0 space-y-1" disabled={unknownOnly}>
      <legend className="mb-1 text-sm font-medium">{FILTER_LABELS[facet]}</legend>
      <div className={`overflow-y-auto overscroll-contain rounded-md border border-input bg-background p-1 ${facet === "qualities" ? "max-h-48" : facet === "region" ? "max-h-[30rem]" : "max-h-28"}`}>
        {options.map(value => <label key={value} className={`flex min-h-8 cursor-pointer items-start gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted ${unknownOnly ? "opacity-60" : ""}`}>
          <input type="checkbox" checked={filters[facet].includes(value)} className="mt-0.5 shrink-0 accent-primary"
            onChange={event => {
              const checked = event.target.checked;
              setFilters(previous => ({ ...previous, [facet]: checked ? [...previous[facet], value] : previous[facet].filter(item => item !== value) }));
            }} />
          <span className="min-w-0 break-words">{value}</span>
        </label>)}
      </div>
    </fieldset>;
  };

  return <aside aria-label="Available active voices" className="min-w-0 rounded-lg border bg-muted/30 p-4">
    <div className="grid items-start gap-4 grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
    <section aria-label="Voice filters" className="min-w-0 space-y-3">
    <VoiceGeographyFilter groups={geographyOptions} selected={filters.geography}
      onChange={geography => setFilters(previous => ({ ...previous, geography }))} />
    {(["gender", "pitch", "qualities"] as VoiceFacet[]).map(renderFilter)}
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-xs text-muted-foreground" aria-live="polite">{filtered.length} of {voices.length} voices</p>
      <Button type="button" size="sm" variant="outline" disabled={!hasFilters} onClick={() => setFilters({ ...EMPTY_VOICE_FILTERS })}>Clear filters</Button>
    </div>
    </section>
    <section aria-label="Voice previews" className="min-w-0">
    <h4 className="mb-2 text-sm font-medium">Voice actor</h4>
    {/* An interactive grid exposes the per-row play buttons to assistive tools;
        listbox options would hide nested buttons from screen readers. */}
    <div ref={listRef} role="grid" aria-label="Available voices" aria-rowcount={filtered.length} aria-colcount={2}
      aria-controls={`${id}-sample`} onKeyDown={navigateVoices}
      className="h-80 overflow-y-auto overscroll-contain rounded-md border border-input bg-background p-1 text-sm sm:h-[30rem]">
      {filtered.map((voice, index) => {
        const active = previewingVoiceId === voice.id;
        const isSelected = selected?.id === voice.id;
        const name = catalogVoiceName(voice);
        const previewLabel = `${active ? "Stop" : "Play"} preview of ${name}`;
        return <div key={voice.id} role="row" aria-selected={isSelected} aria-rowindex={index + 1} data-voice-row={index}
          className={`flex min-h-10 items-center gap-1 rounded ${isSelected ? "bg-primary/15 ring-1 ring-inset ring-primary/40" : "hover:bg-muted"}`}>
          <div role="gridcell" className="min-w-0 flex-1">
            <button type="button" tabIndex={isSelected ? 0 : -1} onFocus={() => selectVoice(voice)} onClick={() => selectVoice(voice)}
              draggable={Boolean(onVoiceDragStart)} title={onVoiceDragStart ? `Drag ${name} onto a character to assign` : undefined}
              onDragStart={event => { selectVoice(voice); onStop(); onVoiceDragStart?.(voice, event); }} onDragEnd={onVoiceDragEnd}
              className={`w-full break-words rounded px-2 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${onVoiceDragStart ? "cursor-grab active:cursor-grabbing" : ""}`}>{name}</button>
          </div>
          <div role="gridcell" className="shrink-0 pr-1">
            <Button type="button" size="icon" variant={active ? "default" : "ghost"} className="h-8 w-8" data-preview="true"
              tabIndex={isSelected ? 0 : -1} disabled={!voice.sample_audio_url} aria-label={previewLabel}
              title={voice.sample_audio_url ? previewLabel : `No saved preview for ${name}`}
              onFocus={() => selectVoice(voice)} onClick={() => { selectVoice(voice); catalogPreviewId.current = voice.id; onPreview(voice); }}>
              {active ? previewStatus === "loading" ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> : <Square aria-hidden="true" className="h-4 w-4" /> : <Play aria-hidden="true" className="h-4 w-4" />}
            </Button>
          </div>
        </div>;
      })}
      {!filtered.length && <p role="status" className="p-3 text-sm">No voices match. Clear a filter to see more voices.</p>}
    </div>
    {previewingVoiceId && <p role="status" className="mt-2 text-xs text-muted-foreground">
      {previewStatus === "loading" ? "Loading saved preview…" : "Playing saved preview"}
    </p>}
    {previewError && <p role="alert" className="mt-2 text-sm text-destructive">{previewError}</p>}
    <section id={`${id}-sample`} aria-label="Sample dialogue" className="mt-4 border-t pt-3">
      <h4 className="text-xs font-medium">Sample dialogue{selected ? ` · ${catalogVoiceName(selected)}` : ""}</h4>
      <p className="mt-2 text-sm text-muted-foreground">{selected?.test_phrase || "Select a voice with saved sample dialogue."}</p>
    </section>
    </section>
    </div>
  </aside>;
}
